import { Link, PageProps, graphql } from 'gatsby'
import React from 'react'

import { getSrc } from 'gatsby-plugin-image'
import styled from 'styled-components'
import StyledEmbeddedEntry from '../components/EmbeddedEntry/StyledEmbeddedEntry'
import theme from '../components/atoms/theme'
import { PaddedContainer } from '../components/container'
import Layout from '../components/layout'
import CenteredPageHeader from '../components/molecules/centeredPageHeader'
import SEO from '../components/molecules/seo'
import ImageBlock from '../components/organisms/ImageBlock'
import { ImageGridBlock } from '../components/organisms/ImageGridBlock'
import { ChatBot } from '../components/organisms/chatBot'
import { ColumnBlock } from '../components/organisms/columnBlock'
import { ContentSet } from '../components/organisms/contentSet'
import { IframeBlock } from '../components/organisms/iframeBlock'
import ImageGallery from '../components/organisms/imageGallery'
import PageHeader from '../components/organisms/pageHeader'
import Spotlight from '../components/organisms/spotlight'
import { isChatbotEnabled } from '../helpers/chatbot'
import GetFocalRatio from '../helpers/getFocalRatio'
import { isContentfulType } from '../lib/contentful'
import { BlockContainer } from '../pages/index'
import { GreyBackground } from '../pages/whats-on'

const { breakpoints, colours } = theme

export const Head: React.FC<
  PageProps<Queries.PageCategoryTemplateQuery, PageCategoryTemplateContextTypes>
> = ({ data, pageContext }) => {
  const { title } = pageContext

  const headerBlock = data.customPageBlocks.nodes[0].body.find((block) =>
    isContentfulType<Queries.ContentfulPageHeaderBlock>(
      block,
      'ContentfulPageHeaderBlock',
    ),
  )
  const leadingParagraph = headerBlock?.leadingParagraph

  const heroImageBlock = data.customPageBlocks.nodes[0].body.find((block) =>
    isContentfulType<Queries.ContentfulHeroPageHeaderBlock>(
      block,
      'ContentfulHeroPageHeaderBlock',
    ),
  )
  const heroImage = heroImageBlock?.heroImage?.image?.gatsbyImageData
  return (
    <SEO
      title={title}
      description={leadingParagraph?.childMarkdownRemark?.rawMarkdownBody}
      imageUrl={heroImage && getSrc(heroImage)}
    />
  )
}

type CustomPageTemplateContextTypes = {
  slug: string
  title: string
}

export const StyledPaddedContainer = styled(PaddedContainer)`
  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 3.6rem 0;
  }
`

export const StyledBlockContainer = styled(BlockContainer)`
  padding: 0;
  overflow-x: hidden;
  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 0 1.6rem;
  }
`

const SpotlightBlockContainer = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
`

export const WhiteContainer = styled(StyledBlockContainer)`
  background-color: ${colours.light};
`

const CustomPageTemplate: React.FC<
  PageProps<Queries.CustomPageTemplateQuery, CustomPageTemplateContextTypes>
> = ({ data, pageContext }) => {
  const { slug, title } = pageContext
  const contentBlocks = data.customPageBlocks.nodes[0].body
  const dynamicSetNodes = {
    whatsOnSectionItems: data.whatsOnSectionItems.edges,
    learningSectionItems: data.learningSectionItems.edges,
    exhibitionSectionItems: data.exhibitionSectionItems.edges,
  }
  const spotlightEvent = data?.spotlightEvent?.edges[0]?.node
  const spotlightPage = data?.spotlightPage?.edges[0]?.node

  // Assuming title is a string variable
  // Assuming Link is a component that takes a to prop of type string
  const renderBreadcrumbs = (slug: string, title: string): JSX.Element => {
    return (
      <>
        <Link to='/' aria-label='Home'>
          Home /
        </Link>
        <Link to={`/${slug}/`} aria-label={title} /> {title}
      </>
    )
  }

  return (
    <Layout>
      {contentBlocks.reduce((selectedBlock, block) => {
        if (
          isContentfulType<Queries.ContentfulPageHeaderBlock>(
            block,
            'ContentfulPageHeaderBlock',
          )
        ) {
          // If a ContentfulPageHeaderBlock is found, return the PageHeader component
          return (
            <PageHeader
              data={{
                title: block?.title,
                slug: slug,
                leadingParagraph: block?.leadingParagraph,
              }}
              breadcrumbs={renderBreadcrumbs(slug, title)}
            />
          )
        }
        // If not a ContentfulPageHeaderBlock, continue with the previous selectedBlock
        return selectedBlock
      }, null)}

      <GreyBackground>
        {contentBlocks.reduce((selectedBlock, block) => {
          if (
            isContentfulType<Queries.ContentfulHeroPageHeaderBlock>(
              block,
              'ContentfulHeroPageHeaderBlock',
            )
          ) {
            // If a ContentfulHeroPageHeaderBlock is found, return the HeroPageHeader component
            return <CenteredPageHeader block={block} />
          }
          // If not a ContentfulPageHeaderBlock, continue with the previous selectedBlock
          return selectedBlock
        }, null)}
        {/* {featuredPage && (

        )} */}
        <StyledPaddedContainer>
          {contentBlocks?.map((block) => {
            if (
              isContentfulType<Queries.ContentfulSpotlightBlock>(
                block,
                'ContentfulSpotlightBlock',
              )
            ) {
              // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
              let spotlight
              if (block.dynamic) {
                if (block.dynamicType === DynamicType.EVENT) {
                  spotlight = spotlightEvent
                } else {
                  spotlight = spotlightPage
                }
              } else {
                spotlight = block.manualSpotlight?.[0]
              }

              // Check that spotlight is not undefined
              if (!spotlight) return null

              // Currently, Spotlight can be page, custom page, or event
              const isPage = spotlight.__typename === 'ContentfulPage'
              const isCustomPage =
                spotlight.__typename === 'ContentfulCustomPage'

              // Handle categories for ContentfulPage
              const categories =
                isPage &&
                (spotlight.categories || spotlight.category || undefined)

              // Handle slug based on page or custom page type
              const slug = isPage
                ? [
                    spotlight.category?.pageCategoryParent?.slug,
                    spotlight.category?.slug,
                    spotlight.slug,
                  ]
                    .filter((_) => _)
                    .join('/')
                : isCustomPage
                  ? spotlight.slug // For custom page, just use slug directly
                  : spotlight.slug // For event, use the slug

              // Custom page might have linked hero blocks, try to find and use heroPageHeaderBlock's heroImage
              const heroImage = isCustomPage
                ? spotlight.body?.find(
                    (block) =>
                      block.__typename === 'ContentfulHeroPageHeaderBlock',
                  )?.heroImage || null // Fallback to null if no hero image found
                : spotlight.imageWithFocalPoint?.image || spotlight.heroImage // For regular page

              // Focal ratio only applies to regular page's imageWithFocalPoint
              const focalRatio =
                isPage && spotlight.imageWithFocalPoint
                  ? GetFocalRatio(spotlight.imageWithFocalPoint)
                  : isCustomPage && heroImage && heroImage.focalPoint
                    ? GetFocalRatio(heroImage)
                    : undefined

              const description = isPage
                ? spotlight.leadingParagraph?.childMarkdownRemark
                    ?.rawMarkdownBody || ''
                : isCustomPage
                  ? spotlight.body?.find(
                      (block) =>
                        block.__typename === 'ContentfulPageHeaderBlock',
                    )?.leadingParagraph.childMarkdownRemark?.rawMarkdownBody ||
                    null
                  : ''

              // Render the spotlight block
              return (
                <SpotlightBlockContainer key={block.contentful_id}>
                  <StyledEmbeddedEntry noPadding={true}>
                    <Spotlight
                      preTitle={spotlight.preTitle}
                      title={spotlight.title}
                      slug={slug}
                      image={isCustomPage ? heroImage.image : heroImage} // Image from either page, custom page's block, or fallback
                      focalRatio={focalRatio} // Only applicable for regular pages
                      leadingParagraph={description}
                      type={
                        isPage || isCustomPage
                          ? 'page'
                          : spotlight.eventType.slug
                      }
                      categories={categories} // Only for pages
                      isFlipped={block.flipOrder}
                    />
                  </StyledEmbeddedEntry>
                </SpotlightBlockContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulContentTileBlock>(
                block,
                'ContentfulContentTileBlock',
              )
            ) {
              return (
                <StyledBlockContainer key={block.contentful_id}>
                  <StyledEmbeddedEntry noPadding={true}>
                    <ContentSet
                      block={block}
                      dynamicSetNodes={dynamicSetNodes}
                    />
                  </StyledEmbeddedEntry>
                </StyledBlockContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulCuratedContentTileBlock>(
                block,
                'ContentfulCuratedContentTileBlock',
              )
            ) {
              return (
                <StyledBlockContainer key={block.contentful_id}>
                  <StyledEmbeddedEntry noPadding={true}>
                    <ContentSet
                      block={block}
                      nodes={block.tiles}
                      hideSliderOnMobile={true}
                      short={true}
                    />
                  </StyledEmbeddedEntry>
                </StyledBlockContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulImageGridBlock>(
                block,
                'ContentfulImageGridBlock',
              )
            ) {
              return (
                <WhiteContainer key={block.contentful_id}>
                  <ImageGridBlock data={block} />
                </WhiteContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulChatBotBlock>(
                block,
                'ContentfulChatBotBlock',
              ) &&
              isChatbotEnabled()
            ) {
              return (
                <StyledBlockContainer key={block.contentful_id}>
                  <ChatBot endpoint={process.env.GATSBY_CHATBOT_ENDPOINT} />
                </StyledBlockContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulImageGallery>(
                block,
                'ContentfulImageGallery',
              )
            ) {
              const { images } = block
              if (images && images.length > 0) {
                return (
                  <StyledBlockContainer key={block.contentful_id}>
                    <ImageGallery images={images} />
                  </StyledBlockContainer>
                )
              }
            }

            if (
              isContentfulType<Queries.ContentfulContentSetBlock>(
                block,
                'ContentfulContentSetBlock',
              )
            ) {
              return (
                <StyledBlockContainer key={block.contentful_id}>
                  <ColumnBlock block={block} />
                </StyledBlockContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulImageBlock>(
                block,
                'ContentfulImageBlock',
              )
            ) {
              return (
                <StyledBlockContainer key={block.contentful_id}>
                  <ImageBlock block={block} />
                </StyledBlockContainer>
              )
            }

            if (
              isContentfulType<Queries.ContentfulIframeBlock>(
                block,
                'ContentfulIframeBlock',
              )
            ) {
              return (
                <StyledBlockContainer key={block.contentful_id}>
                  <IframeBlock data={block} />
                </StyledBlockContainer>
              )
            }
          })}
        </StyledPaddedContainer>
      </GreyBackground>
    </Layout>
  )
}

export default CustomPageTemplate

export const contentfulPageFragment = graphql`
  fragment ContentfulPageFragment on ContentfulPage {
    __typename
    title
    slug
    category {
      slug
      colour
      title
      pageCategoryParent {
        slug
      }
    }
    heroImage {
      title
      url
      gatsbyImageData(layout: CONSTRAINED, quality: 80)
    }
    imageWithFocalPoint {
      focalPoint {
        focalPoint {
          x
          y
        }
      }
      image {
        file {
          details {
            image {
              height
              width
            }
          }
        }
        url
        gatsbyImageData(layout: CONSTRAINED, quality: 80)
      }
    }
    leadingParagraph {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
  }
`

export const contentfulCustomPageFragment = graphql`
  fragment ContentfulCustomPageFragment on ContentfulCustomPage {
    __typename
    body {
      ... on Node {
        ... on ContentfulContentTileBlock {
          ...ContentfulContentTileBlockFragment
        }
        ... on ContentfulCuratedContentTileBlock {
          ...ContentfulCustomCuratedContentTileBlockFragment
        }
        ... on ContentfulSpotlightBlock {
          ...ContentfulCustomSpotlightBlockFragment
        }
        ... on ContentfulImageGridBlock {
          ...ContentfulImageGridBlockFragment
        }
        ... on ContentfulChatBotBlock {
          ...ContentfulChatBotBlockFragment
        }
        ... on ContentfulPageHeaderBlock {
          ...ContentfulPageHeaderBlockFragment
        }
        ... on ContentfulHeroPageHeaderBlock {
          ...ContentfulHeroPageHeaderBlockFragment
        }
        ... on ContentfulImageGallery {
          ...ContentfulImageGalleryFragment
        }
        ... on ContentfulContentSetBlock {
          ...ContentfulContentSetBlockFragment
        }
        ... on ContentfulImageBlock {
          ...ContentfulImageBlockFragment
        }
        ... on ContentfulIframeBlock {
          ...ContentfulIframeBlockFragment
        }
      }
    }
    title
    slug
  }
`

export const contentfulCustomSpotlightBlockFragment = graphql`
  fragment ContentfulCustomSpotlightBlockFragment on ContentfulSpotlightBlock {
    contentful_id
    __typename
    title
    id
    dynamic
    dynamicType
    flipOrder
    manualSpotlight {
      __typename
      ... on Node {
        ... on ContentfulPage {
          ...ContentfulPageFragment
        }
        ... on ContentfulEvent {
          ...ContentfulEventFragment
        }
        ... on ContentfulCustomPage {
          __typename
          body {
            ... on Node {
              ... on ContentfulPageHeaderBlock {
                ...ContentfulPageHeaderBlockFragment
              }
              ... on ContentfulHeroPageHeaderBlock {
                ...ContentfulHeroPageHeaderBlockFragment
              }
            }
          }
          title
          slug
        }
      }
    }
  }
`

export const contentfulContentTileBlockFragment = graphql`
  fragment ContentfulContentTileBlockFragment on ContentfulContentTileBlock {
    id
    __typename
    title
    contentful_id
    sys {
      contentType {
        sys {
          type
        }
      }
    }
    link
    linkUrl
    dynamicContent
  }
`

export const contentfulCustomCuratedContentTileBlockFragment = graphql`
  fragment ContentfulCustomCuratedContentTileBlockFragment on ContentfulCuratedContentTileBlock {
    id
    __typename
    title
    contentful_id
    sys {
      contentType {
        sys {
          type
        }
      }
    }
    link
    linkUrl
    tiles {
      ... on Node {
        ... on ContentfulPage {
          ...ContentfulPageFragment
        }
        ... on ContentfulEvent {
          ...ContentfulEventFragment
        }
      }
    }
  }
`

export const contentfulPageHeaderBlockFragment = graphql`
  fragment ContentfulPageHeaderBlockFragment on ContentfulPageHeaderBlock {
    id
    __typename
    contentful_id
    sys {
      contentType {
        sys {
          type
        }
      }
    }
    title
    leadingParagraph {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
  }
`

export const contentfulIframeBlockFragment = graphql`
  fragment ContentfulIframeBlockFragment on ContentfulIframeBlock {
    id
    __typename
    contentful_id
    title
    url
    height
  }
`

export const contentfulHeroPageHeaderBlockFragment = graphql`
  fragment ContentfulHeroPageHeaderBlockFragment on ContentfulHeroPageHeaderBlock {
    id
    __typename
    contentful_id
    sys {
      contentType {
        sys {
          type
        }
      }
    }
    contentful_id
    firstColContent {
      raw
      references {
        ...ContentfulButtonFragment
      }
    }
    firstColLink
    firstColLinkUrl
    heroImage {
      title
      id
      focalPoint {
        focalPoint {
          x
          y
        }
      }
      image {
        file {
          details {
            image {
              height
              width
            }
          }
        }
        url
        gatsbyImageData(layout: CONSTRAINED, quality: 80)
      }
    }
    secondColContent {
      raw
      references {
        ...ContentfulButtonFragment
      }
    }
    secondColLink
    secondColLinkUrl
    thirdColContent {
      raw
      references {
        ...ContentfulButtonFragment
      }
    }
  }
`

export const query = graphql`
  query CustomPage($slug: String, $now: Date) {
    customPageBlocks: allContentfulCustomPage(filter: { slug: { eq: $slug } }, limit: 1) {
      nodes {
        body {
          ... on Node {
            ... on ContentfulContentTileBlock {
              ...ContentfulContentTileBlockFragment
            }
            ... on ContentfulCuratedContentTileBlock {
              ...ContentfulCustomCuratedContentTileBlockFragment
            }
            ... on ContentfulSpotlightBlock {
              ...ContentfulCustomSpotlightBlockFragment
            }
            ... on ContentfulImageGridBlock {
              ...ContentfulImageGridBlockFragment
            }
            ... on ContentfulChatBotBlock {
              ...ContentfulChatBotBlockFragment
            }
            ... on ContentfulPageHeaderBlock {
              ...ContentfulPageHeaderBlockFragment
            }
            ... on ContentfulHeroPageHeaderBlock {
              ...ContentfulHeroPageHeaderBlockFragment
            }
            ... on ContentfulImageGallery {
              ...ContentfulImageGalleryFragment
            }
            ... on ContentfulContentSetBlock {
              ...ContentfulContentSetBlockFragment
            }
            ... on ContentfulImageBlock {
              ...ContentfulImageBlockFragment
            }
            ... on ContentfulIframeBlock {
              ...ContentfulIframeBlockFragment
            }
          }
        }
        title
        slug
      }
    }
    whatsOnSectionItems: allContentfulEvent(
      limit: 3
      sort: { startDate: DESC }
      filter: { endDate: { gte: $now }, startDate: { ne: null } }
    ) {
      edges {
        node {
          id
          title
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          eventType {
            title
            slug
          }
          slug
          categories {
            title
            slug
            id
            colour
          }
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, quality: 80)
            }
          }
        }
      }
    }
    learningSectionItems: allContentfulPage(filter: { category: { slug: { eq: "learning" } } }, limit: 3) {
      edges {
        node {
          id
          title
          slug
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
          }
          category {
            title
            slug
            colour
          }
          leadingParagraph {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
        }
      }
    }
    exhibitionSectionItems: allContentfulEvent(filter: { eventType: { slug: { eq: "exhibition" } } }, limit: 3) {
      edges {
        node {
          id
          title
          slug
          leadingParagraph {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          categories {
            title
            slug
            id
            colour
          }
          eventType {
            title
            slug
          }
          startDate
          endDate
          customDateText
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
          }
        }
      }
    }
    spotlightEvent: allContentfulEvent(
      filter: { isSpotlightOnHomepage: { eq: true } }
      limit: 1
      sort: { updatedAt: DESC }
    ) {
      edges {
        node {
          ...ContentfulEventFragment
        }
      }
    }
    spotlightPage: allContentfulPage(
      filter: { isSpotlightOnHomepage: { eq: true } }
      limit: 1
      sort: { updatedAt: DESC }
    ) {
      edges {
        node {
          ...ContentfulPageFragment
        }
      }
    }
  }
`
